import _ from "lodash";
import { createSlice, createSelector, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { doc, collection, query, where, limit, getDocs, updateDoc, setDoc, addDoc, deleteDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { Order, OrderStatus, OrderItem, Item, ItemStatus } from "@/types";
import { calculateOrderTotals, getOrderSubtotal } from "@/utils/order";
import { RootState } from "@/store/store";

export const initialState = {
  items: [],
  subtotal: 0,
  taxes: 0,
  areTipsIncluded: true,
  tipPercentage: 10,
  total: 0,
  createdBy: undefined,
  deliveryFee: undefined,
  branch: undefined,
};

// type CreateOrderPayload = Pick<Order, "tableId" | "organizationId">;
interface createEmptyOrderPayload {
  organizationId?: string;
  id: string | null;
}

// ✅ checked
export const createEmptyOrder = createAsyncThunk("orders/createEmptyOrder", async (order: createEmptyOrderPayload, { getState }) => {
  const { auth, order: currentOrder } = getState() as RootState;
  const dineMode = currentOrder.flow;

  if (!order?.organizationId) return;

  const payload: any = {
    status: OrderStatus.DRAFT,
    number: _.times(4, () => _.random(0, 9)).join(""),
    createdBy: auth?.user?.uid ?? "anonymous",
    createdAt: Date.now(),
    flow: dineMode ?? "",
    // branch: currentOrder.branch ?? null,
    ...(currentOrder.branch ? { branch: _.pick(currentOrder.branch, ["id", "title", "location", "whatsapp"]) } : {}),
  };

  try {
    if (order?.id) {
      // Create order based on table id
      payload.id = order.id;

      const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.id}`);
      await setDoc(docRef, payload);
      return { ...payload, id: order.id };
    } else {
      // Create new order
      const colRef = collection(db, `organizations/${order.organizationId}/orders`);
      const snapshot = await addDoc(colRef, payload);

      return { ...payload, id: snapshot.id };
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export interface AddItemsToOrder {
  items: OrderItem[];
  organizationId?: string;
}

// ✅ checked
export const addItemsToOrder = createAsyncThunk("orders/addItemToOrder", async ({ items, organizationId }: AddItemsToOrder, { getState }) => {
  const { order, auth } = getState() as RootState;

  try {
    // if (!items.length || !order?.id || !organizationId) return;
    if (!items.length || !order?.id || !organizationId) {
      throw new Error("Missing required data");
    }

    const newItems = items.map((item) => ({
      ...item,
      price: Number(item.price),
      status: ItemStatus.DRAFT,
    }));

    const docRef = doc(db, `organizations/${organizationId}/orders/${order.id}`);

    const updatedItems = order.items ? [...order.items, ...newItems] : newItems;

    const { subtotal, taxes, total } = calculateOrderTotals(updatedItems, order.tipPercentage, order.areTipsIncluded);

    const payload = {
      lastUpdatedBy: auth?.user?.uid ?? "anonymous",
      lastUpdatedAt: Date.now(),
      items: updatedItems,
      subtotal,
      taxes,
      total,
    };

    await updateDoc(docRef, payload);

    return updatedItems;
  } catch (error) {
    console.error(error);
    throw error;
    // return { error };
  }
});

// ✅ checked *used by waiter*
export const removeItemFromOrder = createAsyncThunk("orders/removeItemFromOrder", async ({ itemId, organizationId }: { itemId: string; organizationId?: string }, { getState }) => {
  try {
    const { order, auth } = getState() as RootState;
    if (!itemId || !order?.items?.length || !order?.id || !organizationId) return;

    const newItems = order.items.filter(({ id }) => id !== itemId);

    const { subtotal, taxes, total } = calculateOrderTotals(newItems, order.tipPercentage, order.areTipsIncluded);

    const payload = {
      lastUpdatedBy: auth?.user?.uid ?? "anonymous",
      lastUpdatedAt: Date.now(),
      items: newItems,
      subtotal,
      taxes,
      total,
    };

    const docRef = doc(db, `organizations/${organizationId}/orders/${order.id}`);
    await updateDoc(docRef, payload);

    return newItems;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

// ✅ checked
export const requestItemsReview = createAsyncThunk("orders/requestItemsReview", async (_, { getState }) => {
  const { auth, order } = getState() as RootState;

  if (!order?.tableId || !order?.organizationId || !order?.items || !order?.items?.length) return;

  const newItems = order.items.map((item) => ({
    ...item,
    status: item.status === ItemStatus.DRAFT ? ItemStatus.REVIEWING : item.status,
  }));

  if (!items.length) return;

  const payload = {
    lastUpdatedBy: auth?.user?.uid ?? "anonymous",
    lastUpdatedAt: Date.now(),
    items: newItems,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);
});

export const getOrdersByTable = createAsyncThunk("orders/getOrdersByTable", async (tableId: string) => {
  try {
    const q = query(collection(db, "orders"), where("tableId", "==", tableId), limit(1));
    const querySnapshot = await getDocs(q);
    const docs: Order[] = [];

    querySnapshot.forEach((doc) => {
      docs.push({ ...(doc.data() as Order), id: doc.id });
    });

    return docs[0] as Order;
  } catch (error) {
    console.error(error);
  }
});

interface GetOrderById {
  organizationId: string;
  orderId: string;
}

export const getOrderById = createAsyncThunk("orders/getOrderById", async ({ organizationId, orderId }: GetOrderById) => {
  try {
    const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return { ...snapshot.data(), id: orderId };
    } else {
      throw new Error("Order does not exist");
    }
  } catch (error) {
    return error;
  }
});

// ✅ checked -This is not been used-
export const markReviewingItemsAsPreparing = createAsyncThunk("orders/markReviewingItemsAsPreparing", async (_, { getState }) => {
  const { auth, order } = getState() as RootState;

  if (!auth.user?.uid || !order?.tableId || !order?.organizationId || !order?.items || !items.length) return;

  const updatedItems = order.items.map((item) => ({
    ...item,
    status: item.status === ItemStatus.REVIEWING ? ItemStatus.PREPARING : item.status,
  }));

  if (!items.length) return;

  const payload = {
    lastUpdatedBy: auth.user.uid,
    lastUpdatedAt: Date.now(),
    items: updatedItems,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);
});
// ✅ checked
// change order status to delivered instead of paid -This is not been
export const markOrderAsPaid = createAsyncThunk("orders/markOrderAsPaid", async (order: Order, { getState }) => {
  const { auth } = getState() as RootState;

  // if (!order || !auth?.user?.uid || !order?.organizationId) return;
  if (!order || !order?.organizationId) return; // TODO: add auth again when create login

  const newOrder = {
    ...order,
    paidBy: auth?.user?.uid,
    paidAt: Date.now(),
    status: OrderStatus.DELIVERED,
  };

  const paidOrdersRef = collection(db, `organizations/${order.organizationId}/paidOrders`);

  addDoc(paidOrdersRef, newOrder)
    .then(() => {
      const unpaidOrdersRef = doc(db, `organizations/${order.organizationId}/orders/${order.id}`);

      deleteDoc(unpaidOrdersRef).catch((error) => {
        console.error(error);
      });
    })
    .catch((error) => {
      console.error(error);
    });
});

//

interface ChangeItemsStagePayload {
  order: Order;
  orderItems: OrderItem[];
}

// ✅ checked
export const markItemsAsPreparing = createAsyncThunk("orders/markItemsAsPreparing", async ({ order, orderItems }: ChangeItemsStagePayload, { getState }) => {
  const { auth } = getState() as RootState;

  if (!auth.user || !order?.tableId || !order?.organizationId || !order?.items || !orderItems?.length) return;

  const selectedItems = orderItems.map(({ id }) => id);

  const updatedItems = order.items.map((item) => ({
    ...item,
    status: selectedItems.includes(item.id) ? ItemStatus.PREPARING : item.status,
  }));

  const payload = {
    lastUpdatedBy: auth.user.uid,
    lastUpdatedAt: serverTimestamp(),
    items: updatedItems,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);
});

// ✅ checked
export const markItemsAsDelivered = createAsyncThunk("orders/markItemsAsDelivered", async ({ order, orderItems }: ChangeItemsStagePayload, { getState }) => {
  const { auth } = getState() as RootState;

  if (!auth.user || !order?.tableId || !order?.organizationId || !order?.items || !orderItems?.length) return;

  const selectedItems = orderItems.map(({ id }) => id);

  const updatedItems = order.items.map((item) => ({
    ...item,
    status: selectedItems.includes(item.id) ? ItemStatus.DELIVERED : item.status,
  }));

  const payload = {
    lastUpdatedBy: auth.user.uid,
    lastUpdatedAt: serverTimestamp(),
    items: updatedItems,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);
});

// ✅ checked
export const markItemsAsPaid = createAsyncThunk("orders/markItemsAsPaid", async ({ order, orderItems }: ChangeItemsStagePayload, { getState, dispatch }) => {
  const { auth } = getState() as RootState;

  if (!auth.user || !order?.tableId || !order?.organizationId || !order?.items || !orderItems?.length) return;

  const selectedItems = orderItems.map(({ id }) => id);

  const updatedItems = order.items.map((item) => ({
    ...item,
    status: selectedItems.includes(item.id) ? ItemStatus.PAID : item.status,
  }));

  const paidItems = updatedItems.filter((i) => i.status === ItemStatus.PAID);
  const isOrderPaid = paidItems.length === updatedItems.length;

  const payload = {
    lastUpdatedBy: auth.user.uid,
    lastUpdatedAt: serverTimestamp(),
    items: updatedItems,
    status: isOrderPaid ? OrderStatus.PAID : OrderStatus.ACTIVE,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);

  if (isOrderPaid) {
    dispatch(markOrderAsPaid({ ...order, ...payload }));
  }
});

// ✅ checked *used by client*
export const removeItem = createAsyncThunk("orders/removeItem", async (itemId: OrderItem["id"], { getState }) => {
  const { auth, order } = getState() as RootState;

  if (!itemId || !order?.organizationId || !order?.id || !order?.items) return;

  const selectedItems = order.items.map(({ id }) => id);
  const updatedItems = order.items.filter(({ id }) => !selectedItems.includes(id));

  const { subtotal, taxes, total } = calculateOrderTotals(updatedItems, order.tipPercentage, order.areTipsIncluded);

  const payload = {
    lastUpdatedBy: auth?.user?.uid ?? "anonymous",
    lastUpdatedAt: serverTimestamp(),
    items: updatedItems,
    subtotal,
    taxes,
    total,
  };

  const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
  await updateDoc(docRef, payload);
});

// change tip *Pending*
export const updateTip = createAsyncThunk("orders/updateTip", async (_, { getState }) => {
  const { order, auth } = getState() as RootState;

  if (!order || !order?.organizationId || !order?.tableId || !order?.items || !order?.items?.length) return;

  const newTip = {
    ...order,
    tipPercentage: order.tipPercentage ? order.tipPercentage : initialState.tipPercentage,
  };

  if (!order.tipPercentage) return;

  const payload = {
    lastUpdatedBy: auth?.user?.uid ?? "anonymous",
    lastUpdatedAt: serverTimestamp(),
    tipPercentage: newTip,
  };

  try {
    const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
    await updateDoc(docRef, payload);
  } catch (error) {
    console.error(error);
  }
});

export const OrderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    addTips: (state) => {
      state.areTipsIncluded = true;
    },
    removeTips: (state) => {
      state.areTipsIncluded = false;
    },
    changetipPercentage: (state, action: PayloadAction<Order["tipPercentage"]>) => {
      state.tipPercentage = action.payload;
    },
    increaseQuantity: (state, action: PayloadAction<OrderItem>) => {
      if (!state.items.length) {
        state.items.push({ ...action.payload, quantity: 1 });
        return state;
      }

      const menuItemIndex = state.items.findIndex((item) => item.id === action.payload.id);

      // If it is added
      if (menuItemIndex >= 0) {
        // Increase quantity
        const menuItem = state.items[menuItemIndex];
        menuItem.quantity = (menuItem?.quantity ?? 0) + 1;
        state.items.splice(menuItemIndex, 1, menuItem);
      } else {
        // If it is not added
        state.items.push({ ...action.payload, quantity: 1 });
      }
    },
    reduceQuantity: (state, action: PayloadAction<Item>) => {
      if (!state.items.length) return state;

      const menuItemIndex = state.items.findIndex((item) => item.id === action.payload.id);

      // If it is added
      if (menuItemIndex >= 0) {
        // Reduce quantity
        const menuItem = state.items[menuItemIndex];
        const newQuantity = menuItem?.quantity && menuItem?.quantity > 0 ? menuItem.quantity - 1 : 0;

        if (newQuantity === 0) {
          state.items.splice(menuItemIndex, 1);
        } else {
          state.items.splice(menuItemIndex, 1, {
            ...menuItem,
            quantity: newQuantity,
          });
        }
      }
    },
    updateOrder: (state, action: PayloadAction<Partial<Order>>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    deleteOrder: () => initialState,
    getLocalOrders: (state, action: PayloadAction<Order>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setOrdersFlow: (state, action: PayloadAction<Order["flow"]>) => {
      state.flow = action.payload;
    },
    setOrdersSource: (state, action: PayloadAction<Order["source"]>) => {
      state.source = action.payload;
    },
    setBranch: (state, action: PayloadAction<Order["branch"]>) => {
      state.branch = action.payload;
    },
    setDeliveryLocation: (state, action: PayloadAction<Order["deliveryLocation"]>) => {
      state.deliveryLocation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrdersByTable.fulfilled, (state, action: PayloadAction<Order | undefined>) => {
      if (!action.payload) return state;
      return action.payload;
    });
    builder.addCase(getOrderById.fulfilled, (state, action: PayloadAction<Order | undefined>) => {
      if (!action.payload) return state;
      return action.payload;
    });
    builder.addCase(createEmptyOrder.fulfilled, (state, action: PayloadAction<Order | undefined>) => {
      if (!action.payload) return state;

      return {
        ...state,
        ...action.payload,
      };
    });
    builder.addCase(addItemsToOrder.fulfilled, (state, action: PayloadAction<OrderItem[] | undefined>) => {
      if (!action.payload) return state;

      return {
        ...state,
        items: action.payload,
      };
    });
    builder.addCase(removeItemFromOrder.fulfilled, (state, action: PayloadAction<OrderItem[] | undefined>) => {
      if (!action.payload) return state;

      return {
        ...state,
        items: action.payload,
      };
    });
    builder.addCase(removeItem.fulfilled, (state, action: PayloadAction<OrderItem[] | undefined>) => {
      if (!action.payload) return state;

      return {
        ...state,
        items: action.payload,
      };
    });
  },
});

const items = (state: Order): OrderItem[] => state.order.items;

const tipPercentage = (state: { order: Order }): number => state.order.tipPercentage;
const areTipsIncluded = (state: { order: Order }): boolean => state.order.areTipsIncluded;

export const subtotalSelector = createSelector([items], (items) => {
  return getOrderSubtotal(items);
});

export const taxesSelector = createSelector([items], (items) => {
  const { taxes } = calculateOrderTotals(items);
  return taxes;
});

export const tipsSelector = createSelector([items, tipPercentage, areTipsIncluded], (items, tipPercentage, areTipsIncluded) => {
  const { tips } = calculateOrderTotals(items, tipPercentage, areTipsIncluded);
  return tips;
});

export const totalSelector = createSelector([items, tipPercentage, areTipsIncluded], (items, tipPercentage, areTipsIncluded) => {
  const { total } = calculateOrderTotals(items, tipPercentage, areTipsIncluded);
  return total;
});


export const pendingReviewSelector = createSelector([items], (items) => {
  return items.filter(({ status }) => status === ItemStatus.REVIEWING);
});

export const { addTips, removeTips, changetipPercentage, increaseQuantity, reduceQuantity, updateOrder, deleteOrder, getLocalOrders, setOrdersFlow, setOrdersSource, setBranch, setDeliveryLocation } = OrderSlice.actions;

export default OrderSlice.reducer;
